<template>
  <div class="connector-consistency">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <b-form-checkbox
        v-model="onlyEnabled"
        size="md"
        :disabled="loading"
        switch
      >
        {{ $t("connector.label.only_enabled") }}
      </b-form-checkbox>
      <b-button
        variant="info"
        :disabled="loading"
        @click="getAllDatas"
      >
        <b-spinner v-if="loading" class="mr-2" small />
        {{ $t("connector.btn.recheck_consistency") }}
      </b-button>
    </div>
    <b-table
      :fields="fields"
      :items="items"
      small
      responsive="sm"
      class="mb-0"
      :tbody-tr-class="!loading ? 'connector-consistency-row' : null"
      :busy="loading"
      @row-clicked="onRowClick($event)"
    >
      <template #table-busy>
        <div class="text-center text-info my-3">
          <b-spinner class="align-middle mr-2" small />
          <strong>{{ $t('connector.label.loading_consistency') }}</strong>
        </div>
      </template>

      <template #cell(anisen)="row">
        {{ row.value }}
        <template v-if="!onlyEnabled && row.item.anisenEnabled">
          <i
            :id="'connector-consistency-anisen-tooltip-' + row.index"
            class="fa-solid fa-circle-info"
          />
          <b-tooltip :target="'connector-consistency-anisen-tooltip-' + row.index">
            {{ $t("connector.label.nb_with_enabled", [row.item.anisenEnabled]) }}
          </b-tooltip>
        </template>
      </template>
      <template #cell(connector)="row">
        {{ row.value }}
        <template v-if="!onlyEnabled && row.item.connectorEnabled">
          <i
            :id="'connector-consistency-connector-tooltip-' + row.index"
            class="fa-solid fa-circle-info"
          />
          <b-tooltip :target="'connector-consistency-connector-tooltip-' + row.index">
            {{ $t("connector.label.nb_with_enabled", [row.item.connectorEnabled]) }}
          </b-tooltip>
        </template>
      </template>
      <template #cell(status)="row">
        <span
          class="status-dot"
          :class="`status-dot-${getStatusColor(row.item.ratio)}`"
          :title="getStatusTitle(row.item.ratio)"
        />
      </template>
    </b-table>
    <modal-consistency-details ref="modalDetails" />
  </div>
</template>

<script>
import ModalConsistencyDetails from './ModalConsistencyDetails.vue';

import { MappedLieux, MappedServices } from '../services/establishment';
import { MappedParticipants } from '../services/participants';
import { MappedStaffs } from '../services/staffs';
import { Activites, Lieux, Personnels, Residents, Secteurs, SousSecteurs } from '../services/netsoins';
import ApiNetsoins from "../services/proxyNetsoins";
import ApiTitan, {
  normalizeTitanPlace,
  normalizeTitanResident,
  normalizeTitanSector,
  normalizeTitanStaff,
} from "../services/titan";
import { GetAllCustomActivities } from '../services/customactivities.js';

const RESIDENT_DOMAIN = "resident";
const STAFF_DOMAIN = "staff";
const SECTOR_DOMAIN = "sector";
const PLACE_DOMAIN = "place";
const ACTIVITY_DOMAIN = "activity";

const RATE_LIMITING_DELAY = 500;

const normalizeActivityLabel = label => label
  ?.trim()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase() || null;

const ANISEN_ITEM_ACCESSORS = {
  [RESIDENT_DOMAIN]: {
    id: item => item.par_id,
    label: item => `${item.par_firstname} ${item.par_lastname.toUpperCase()}`,
    isEnabled: item => item.pare_is_enable && !item.pare_is_delete,
  },
  [STAFF_DOMAIN]: {
    id: item => item.staf_id,
    label: item => `${item.staf_firstname} ${item.staf_lastname.toUpperCase()}`,
    isEnabled: item => item.sest_is_enable && !item.sest_is_delete,
  },
  [SECTOR_DOMAIN]: {
    id: item => item.serv_id,
    label: item => item.serv_name,
    isEnabled: item => item.serv_is_enable,
  },
  [PLACE_DOMAIN]: {
    id: item => item.anp_id,
    label: item => item.anp_label,
    isEnabled: item => item.anp_is_enable,
  },
  [ACTIVITY_DOMAIN]: {
    id: item => item.cae_id,
    label: item => item.cae_label,
    isEnabled: item => item.cae_is_enable,
  },
};

export default {
  name: 'ConnectorConsistency',
  components: { ModalConsistencyDetails },
  props: {
    esta: { type: Number, default: null },
    cnt: { type: Number, default: null },
    type: {
      type: String,
      default: null,
    },
    keyapi: { 
      type: String,
      default: null 
    },
    urlapi: { 
      type: String, 
      default: null 
    },
    useProxy: {
      type: Boolean,
      default: false,
    },
    config: {
      type: Object,
      default() { return {} },
    },
    connector: {
      type: String,
      default: "NETSOINS",
    },
  },
  data() {
    return {
      // FOR ANISEN
      loadingAnisen: true,
      toAnisen: null,
      requestIdAnisen: 0,
      residents: null,
      staffs: null,
      sectors: null,
      places: null,
      activities: null,

      // FOR CONNECTOR
      loadingConnector: true,
      toConnector: null,
      requestIdConnector: 0,
      connectorResidents: null,
      connectorStaffs: null,
      connectorSectors: null,
      connectorPlaces: null,
      connectorActivities: null,

      // OTHER
      onlyEnabled: false,
    };
  },
  computed: {
    fields() {
      return [
        {
          key: 'domain',
          label: this.$t('connector.label.domain'),
          sortable: true,
          tdClass: 'align-middle',
        },
        {
          key: 'anisen',
          label: this.$t('connector.label.anisen_number'),
          sortable: true,
          tdClass: 'align-middle',
        },
        {
          key: 'connector',
          label: this.$t('connector.label.connector_number', [this.connector]),
          sortable: true,
          tdClass: 'align-middle',
        },
        {
          key: 'conform',
          label: this.$t('connector.label.conform_number'),
          sortable: true,
          tdClass: 'align-middle',
        },
        {
          key: 'difference',
          label: this.$t('connector.label.difference_number'),
          sortable: true,
          tdClass: 'align-middle',
        },
        {
          key: 'status',
          label: this.$t('connector.label.status'),
          tdClass: 'align-middle',
        },
      ];
    },
    loading() {
      return this.loadingAnisen || this.loadingConnector;
    },
    items() {
      const createRow = (domain, domainLabel) => {
        let anisenItems = this.getAnisenItems(domain)?.map(item => this.normalizeAnisenItem(domain, item));
        let connectorItems = this.getConnectorItems(domain);

        if (this.onlyEnabled) {
          anisenItems = anisenItems?.filter(aItem => aItem.isEnabled) ?? anisenItems;

          if (domain !== ACTIVITY_DOMAIN) {
            connectorItems = connectorItems?.filter(cItem => cItem.Actif === "1") ?? connectorItems;
          }
        }

        const getAnisenExtId = item => domain === ACTIVITY_DOMAIN
          ? normalizeActivityLabel(item.label)
          : item.ext_uri;
        const getConnectorExtId = item => domain === ACTIVITY_DOMAIN
          ? normalizeActivityLabel(item.Libelle)
          : item.Uri;

        const distinctConnectorExtIds = new Set((connectorItems || []).map(getConnectorExtId).filter(Boolean));
        const anisenCountByExtId = (anisenItems || []).reduce((counts, item) => {
          const extId = getAnisenExtId(item);
          if (!extId) return counts;

          counts.set(extId, (counts.get(extId) || 0) + 1);
          return counts;
        }, new Map());
        const distinctAnisenExtIds = new Set(anisenCountByExtId.keys());

        const details = (anisenItems || []).map(aItem => {
          const extId = getAnisenExtId(aItem);
          let differenceState = "conform";

          if (!extId) differenceState = "not_connected";
          else if (anisenCountByExtId.get(extId) > 1) differenceState = "ambiguous";
          else if (!connectorItems) differenceState = "error";
          else if (!distinctConnectorExtIds.has(extId)) differenceState = "only_anisen";

          const extUri = domain === ACTIVITY_DOMAIN && differenceState === "conform"
            ? connectorItems.find(cItem => getConnectorExtId(cItem) === extId)?.Uri
            : aItem.ext_uri;

          return {
            is_conform: differenceState === "conform",
            id: aItem.id,
            ext_uri: extUri || "-",
            label: aItem.label || "-",
            is_enabled: aItem.isEnabled,
            detail: this.$t(`connector.label.difference.${differenceState}`),
          };
        });

        connectorItems?.forEach(cItem => {
          const extId = getConnectorExtId(cItem);
          if (!distinctAnisenExtIds.has(extId)) {
            details.push({
              is_conform: false,
              id: "-",
              ext_uri: cItem.Uri,
              label: cItem.Libelle ?? `${cItem.Prenom} ${cItem.Nom.toUpperCase()}`,
              is_enabled: domain === ACTIVITY_DOMAIN || cItem.Actif === "1",
              detail: this.$t("connector.label.difference.only_connector", [this.connector]),
            });
            distinctAnisenExtIds.add(extId);
          }
        });
        details.sort((a, b) => a.label.localeCompare(b.label));

        const conformLength = !connectorItems || !anisenItems
          ? null
          : details.filter(dItem => dItem.is_conform).length;

        let difference = (anisenItems?.length - conformLength) + (connectorItems?.length - conformLength);
        if (isNaN(difference)) difference = null;

        return {
          domain: domainLabel,
          anisen: anisenItems?.length ?? "-",
          anisenEnabled: anisenItems?.filter(aItem => aItem.isEnabled)?.length,
          connector: connectorItems?.length ?? "-",
          connectorEnabled: domain === ACTIVITY_DOMAIN ? connectorItems?.length : connectorItems?.filter(cItem => cItem.Actif === "1")?.length,
          conform: conformLength ?? "-",
          difference: difference ?? "-",
          ratio: conformLength / (conformLength + difference),
          details,
        };
      };

      const rows = [
        createRow(RESIDENT_DOMAIN, this.$t('label.residents')),
        createRow(STAFF_DOMAIN, this.$t('label.staffs')),
        createRow(SECTOR_DOMAIN, this.$t('connector.label.secteurs')),
        createRow(PLACE_DOMAIN, this.$t('connector.label.lieux')),
      ];

      if (this.connector === "NETSOINS") {
        rows.push(createRow(ACTIVITY_DOMAIN, this.$t('label.activities')));
      }

      return rows;
    },
  },
  mounted() {
    this.getAllDatas();
  },
  beforeDestroy() {
    clearTimeout(this.toAnisen);
    clearTimeout(this.toConnector);
  },
  methods: {
    getAllDatas() {
      this.getAnisenDatas();
      this.getConnectorDatas();
    },
    getAnisenDatas() {
      clearTimeout(this.toAnisen);

      const requestId = ++this.requestIdAnisen;
      this.loadingAnisen = true;

      this.toAnisen = setTimeout(async () => {
        const fetchDomain = async (domain) => {
          try {
            if (domain === RESIDENT_DOMAIN) return await MappedParticipants(this.esta, this.cnt);
            if (domain === STAFF_DOMAIN) return await MappedStaffs(this.esta, this.cnt);
            if (domain === SECTOR_DOMAIN) return await MappedServices(this.esta, this.cnt);
            if (domain === PLACE_DOMAIN) return await MappedLieux(this.esta, this.cnt);
            if (domain === ACTIVITY_DOMAIN) return await GetAllCustomActivities(this.esta, false);
          }
          catch {}

          return null;
        };

        try {
          const [
            residents,
            staffs,
            sectors,
            places,
            activities,
          ] = await Promise.all([
            fetchDomain(RESIDENT_DOMAIN),
            fetchDomain(STAFF_DOMAIN),
            fetchDomain(SECTOR_DOMAIN),
            fetchDomain(PLACE_DOMAIN),
            this.connector === "NETSOINS"
              ? fetchDomain(ACTIVITY_DOMAIN)
              : null,
          ]);

          if (requestId !== this.requestIdAnisen) return;

          this.residents = residents;
          this.staffs = staffs;
          this.sectors = sectors;
          this.places = places;
          this.activities = activities;
        }
        finally {
          if (requestId === this.requestIdAnisen) this.loadingAnisen = false;
        }
      }, 500);
    },
    getConnectorDatas() {
      clearTimeout(this.toConnector);

      const requestId = ++this.requestIdConnector;
      this.loadingConnector = true;

      this.toConnector = setTimeout(async () => {
        try {
          let api = null;
          if (this.connector === "TITAN") {
            api = new ApiTitan(
              this.config.apilogin,
              this.config.apipassword,
              this.config.apikey,
              this.config.apiurl,
              this.config.ext_uri,
            );
          }
          else if (this.useProxy) {
            api = new ApiNetsoins(this.keyapi, this.urlapi, this.type);
          }

          const fetchDomain = async (domain) => {
            if (this.connector === "TITAN") {
              if (domain === RESIDENT_DOMAIN) return (await api.getResidents()).map(normalizeTitanResident);
              if (domain === STAFF_DOMAIN) return (await api.getStaffs()).map(normalizeTitanStaff);
              if (domain === SECTOR_DOMAIN) return (await api.getSectors()).map(normalizeTitanSector);
              if (domain === PLACE_DOMAIN) return (await api.getPlaces()).map(normalizeTitanPlace);
            }
            else if (this.useProxy) {
              if (domain === RESIDENT_DOMAIN) return await api.getResidents();
              if (domain === STAFF_DOMAIN) return await api.getStaffs();
              if (domain === SECTOR_DOMAIN) return await api.getSectors();
              if (domain === PLACE_DOMAIN) return await api.getPlaces();
              if (domain === ACTIVITY_DOMAIN) return await api.getActivities();
            }
            else {
              const config = {
                uri: this.urlapi,
                key: this.keyapi,
              };

              if (domain === RESIDENT_DOMAIN) return await Residents(config);
              if (domain === STAFF_DOMAIN) return await Personnels(config);
              if (domain === SECTOR_DOMAIN) return await (this.type === "SECTEUR" ? Secteurs(config) : SousSecteurs(config));
              if (domain === PLACE_DOMAIN) return await Lieux(config);
              if (domain === ACTIVITY_DOMAIN) return await Activites(config);
            }

            return null;
          };

          const fetchDomainResult = async domain => {
            try {
              return { data: await fetchDomain(domain), failed: false };
            }
            catch {
              return { data: null, failed: true };
            }
          };

          const domains = [
            RESIDENT_DOMAIN,
            STAFF_DOMAIN,
            SECTOR_DOMAIN,
            PLACE_DOMAIN,
          ];

          if (this.connector === "NETSOINS") domains.push(ACTIVITY_DOMAIN);

          // Initial calls
          const results = await Promise.all(domains.map(fetchDomainResult));

          // Retry failed calls
          for (let i = 0; i < results.length; i++) {
            if (requestId !== this.requestIdConnector) return;

            if (results[i].failed) {
              await new Promise(resolve => setTimeout(resolve, RATE_LIMITING_DELAY));
              if (requestId !== this.requestIdConnector) return;

              results[i] = await fetchDomainResult(domains[i]);
            }
          }

          if (requestId !== this.requestIdConnector) return;

          this.connectorResidents = results[0].data;
          this.connectorStaffs = results[1].data;
          this.connectorSectors = results[2].data;
          this.connectorPlaces = results[3].data;
          this.connectorActivities = results[4]?.data ?? null;
        }
        catch {
          if (requestId === this.requestIdConnector) {
            this.connectorResidents = null;
            this.connectorStaffs = null;
            this.connectorSectors = null;
            this.connectorPlaces = null;
            this.connectorActivities = null;
          }
        }
        finally {
          if (requestId === this.requestIdConnector) this.loadingConnector = false;
        }
      }, 500);
    },
    getAnisenItems(domain) {
      if (domain === RESIDENT_DOMAIN) return this.residents;
      if (domain === STAFF_DOMAIN) return this.staffs;
      if (domain === SECTOR_DOMAIN) return this.sectors;
      if (domain === PLACE_DOMAIN) return this.places;
      if (domain === ACTIVITY_DOMAIN) return this.activities;

      return null;
    },
    getConnectorItems(domain) {
      if (domain === RESIDENT_DOMAIN) return this.connectorResidents;
      if (domain === STAFF_DOMAIN) return this.connectorStaffs;
      if (domain === SECTOR_DOMAIN) return this.connectorSectors;
      if (domain === PLACE_DOMAIN) return this.connectorPlaces;
      if (domain === ACTIVITY_DOMAIN) return this.connectorActivities;

      return null;
    },
    normalizeAnisenItem(domain, item) {
      const accessors = ANISEN_ITEM_ACCESSORS[domain];

      if (!accessors) {
        return {
          id: null,
          ext_uri: item.ext_uri,
          label: null,
          isEnabled: null,
        };
      }

      return {
        id: accessors.id(item),
        ext_uri: item.ext_uri,
        label: accessors.label(item),
        isEnabled: accessors.isEnabled(item),
      };
    },
    getStatusColor(ratio) {
      return ratio == null || isNaN(ratio)
        ? null
        : ratio == 1
          ? "green"
          : ratio > 0.5
            ? "orange"
            : "red";
    },
    getStatusTitle(ratio) {
      return ratio == null || isNaN(ratio)
        ? null
        : `${Math.round(ratio * 100)} %`
    },
    onRowClick(row) {
      this.$refs.modalDetails.show({
        title: row.domain ? this.$t("connector.label.consistency_of", [row.domain.toLowerCase()]) : this.$t("connector.label.consistency"),
        items: row.details,
      });
    },
  },
};
</script>

<style scoped>
.status-dot {
  border-radius: 50%;
  display: inline-block;
  height: 1rem;
  width: 1rem;
  background-color: grey;
}

.status-dot-green {
  background-color: green;
}
.status-dot-orange {
  background-color: orange;
}
.status-dot-red {
  background-color: red;
}
</style>

<style>
.connector-consistency-row:hover {
  cursor: pointer;
  background-color: rgba(0, 0, 0, 0.1);
}
</style>
